import { ApplicationStatusBadge } from "shared/app/ApplicationStatusBadge";
import { Cell } from "./Cell";
import { FullApplicant } from "../types";
import { Show } from "shared/components/helpers/Show";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "providers/Auth";
import {
  Archive,
  Attachment02,
  FlipBackward,
  User03
} from "untitledui-js-base";
import {
  ApplicantStatus,
  ApplicantType,
  ApplicationStatus,
  ApplicationFeeStatus
} from "shared/types";
import { Dispatch, SetStateAction } from "react";
import { getFullName } from "shared/utils/get-full-name";
import { cx } from "shared/utils";

export const ApplicantHeaderCell = ({
  applicant,
  applicationId,
  setCancelApplicantModal,
  setChangeRoleModal,
  setRefundModal,
  applicationStatus,
  eligibleForRoleUpdate,
  allowCoSigner
}: {
  applicant: FullApplicant | { email: string };
  applicationId?: string;
  setCancelApplicantModal: Dispatch<SetStateAction<string | undefined>>;
  setChangeRoleModal: Dispatch<SetStateAction<FullApplicant | undefined>>;
  setRefundModal: Dispatch<SetStateAction<string | undefined>>;
  applicationStatus?: ApplicationStatus;
  eligibleForRoleUpdate?: boolean;
  allowCoSigner: boolean;
}) => {
  const { permissions, type } = useAuthContext();
  const navigate = useNavigate();

  return (
    <Cell className="!h-[138px]" isDataCell>
      <div className="w-full">
        <div className="flex justify-between items-center mb-2 max-w-[340px]">
          <div>
            <ApplicationStatusBadge
              status={
                applicationStatus === ApplicationStatus.Canceled
                  ? ApplicantStatus.Canceled
                  : "first_name" in applicant
                    ? applicant.application_status
                    : undefined
              }
              type="applicant"
              refunded={"refunded" in applicant && applicant.refunded}
            />
          </div>
          <Show if={Boolean(permissions.canViewReports)}>
            <div className="ml-auto">
              <ContextMenuPopover
                contentClassName="z-[1]"
                borderLess={false}
                actions={[
                  {
                    title: "Review",
                    onClick: () => {
                      if (!applicationId)
                        toast.warning(
                          "Applicant received an invite, but didn't apply yet"
                        );
                      else
                        "id" in applicant &&
                          navigate(
                            `/applications/${applicationId}/reports?applicantId=${applicant.id}`
                          );
                    }
                  },
                  ...(type.isAdmin || type.isOwner
                    ? [
                        ...("id" in applicant
                          ? [
                              {
                                className: eligibleForRoleUpdate
                                  ? ""
                                  : "bg-alpha-40",
                                title: (
                                  <span className="flex gap-2 items-center">
                                    <User03
                                      size="20"
                                      className={cx(
                                        eligibleForRoleUpdate
                                          ? "stroke-content-200"
                                          : "stroke-content-100"
                                      )}
                                    />
                                    <p
                                      className={cx(
                                        eligibleForRoleUpdate
                                          ? "text-content-1000"
                                          : "text-content-100"
                                      )}
                                    >
                                      Change role
                                    </p>
                                  </span>
                                ),
                                onClick: () => {
                                  setChangeRoleModal(applicant);
                                },
                                tooltip: allowCoSigner
                                  ? `
                                You can’t change the role for ${
                                  "first_name" in applicant
                                    ? getFullName({
                                        firstName: applicant.first_name || "",
                                        lastName: applicant.last_name || "",
                                        middleName: applicant.middle_name,
                                        short: true
                                      })
                                    : applicant.email
                                } because they are the only applicant in the group.
                                  To change the role, please invite or assign another applicant first.
                                `
                                  : undefined,
                                disabled:
                                  !eligibleForRoleUpdate || !allowCoSigner
                              }
                            ]
                          : []),
                        {
                          title: (
                            <span className="flex gap-2 items-center">
                              <Attachment02
                                size="20"
                                className="stroke-content-200"
                              />
                              Upload documents
                            </span>
                          ),
                          onClick: () => {
                            if (!applicationId)
                              toast.warning(
                                "Applicant received an invite, but didn't apply yet"
                              );
                            else if ("id" in applicant)
                              navigate(
                                `/applications/${applicationId}/reports?applicantId=${applicant.id}&report=additional_documents`
                              );
                          },
                          disabled: !("id" in applicant),
                          tooltip: "Cannot upload documents for invitee"
                        }
                      ]
                    : []),
                  ...(type.isAdmin ||
                  type.isOwner ||
                  permissions.canCancelApplications
                    ? [
                        {
                          title: (
                            <span className="flex gap-2 items-center">
                              <Archive
                                size="20"
                                className="stroke-content-200"
                              />
                              Cancel application
                            </span>
                          ),
                          onClick: () => {
                            if (
                              ![
                                ApplicationStatus.Canceled,
                                ApplicationStatus.Approved,
                                ApplicationStatus.Rejected,
                                ApplicationStatus.ConditionalApproved
                              ].includes(applicationStatus as ApplicationStatus)
                            )
                              setCancelApplicantModal(
                                "id" in applicant
                                  ? String(applicant.id)
                                  : String(applicant.email)
                              );
                          },
                          tooltip:
                            applicationStatus === ApplicationStatus.Canceled
                              ? "Cannot cancel a canceled application"
                              : "You can't cancel application once the decisions have been made",
                          disabled: [
                            ApplicationStatus.Canceled,
                            ApplicationStatus.Approved,
                            ApplicationStatus.Rejected,
                            ApplicationStatus.ConditionalApproved
                          ].includes(applicationStatus as ApplicationStatus)
                        }
                      ]
                    : []),
                  ...(type.isAdmin || type.isOwner
                    ? [
                        {
                          title: (
                            <span className="flex gap-2 items-center">
                              <FlipBackward
                                size="20"
                                className="stroke-content-200"
                              />
                              Refund application
                            </span>
                          ),
                          disabled: !(
                            "application_status" in applicant &&
                            applicant.application_status ===
                              ApplicantStatus.Submitted &&
                            "application_fee_status" in applicant &&
                            applicant.application_fee_status ===
                              ApplicationFeeStatus.Paid
                          ),
                          tooltip:
                            "application_status" in applicant &&
                            applicant.application_status ===
                              ApplicantStatus.Submitted
                              ? "application_fee_status" in applicant &&
                                applicant.application_fee_status ===
                                  ApplicationFeeStatus.Refunded
                                ? "Refund has already been requested for this application"
                                : applicant.application_fee_status ===
                                    ApplicationFeeStatus.Paid
                                  ? ""
                                  : `You can request a refund if the ${
                                      applicant.role === ApplicantType.Applicant
                                        ? "applicant"
                                        : applicant.role ===
                                            ApplicantType.CoSigner
                                          ? "guarantor"
                                          : ""
                                    } has paid the application fee`
                              : "Refund has already been requested for this application",
                          onClick: () =>
                            setRefundModal(
                              "id" in applicant
                                ? String(applicant.id)
                                : undefined
                            )
                        }
                      ]
                    : [])
                ]}
              />
            </div>
          </Show>
        </div>

        <h3 className="text-sm font-bold text-content-1000 mb-2">
          {"first_name" in applicant &&
          applicant.first_name &&
          applicant.last_name
            ? getFullName({
                firstName: applicant.first_name,
                lastName: applicant.last_name,
                middleName: applicant.middle_name,
                short: true
              })
            : applicant.email}
        </h3>

        <p className="text-sm text-content-400">
          {!("role" in applicant) || applicant.role === ApplicantType.Applicant
            ? "Applicant"
            : "Guarantor"}
        </p>
      </div>
    </Cell>
  );
};
